<script setup>
import { computed, ref } from "vue";
import { Link } from "@inertiajs/vue3";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { resolveProfileTheme } from "@/Components/Profile/profileThemes.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import CountIcon from "@/Components/Profile/CountIcon.vue";
import IconGiftHeroBug from "~/svg/badge-gift-hero-bug.svg";
import IconSparkles from "~/svg/badge-sparkles.svg";
import { useInertiaAction } from "@/composables/useInertiaAction.js";

/**
 * Person card for the redesigned Followers / Following grids (GH-851):
 * theme-gradient banner, avatar, counts bar, and Visit / Follow actions.
 */
const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
});

const authUser = useAuthUser();

const isSelf = computed(() => authUser.value?.uuid === props.user.uuid);

// The banner renders the friend's profile theme flattened to the wide-short
// swatch angle, mirroring the theme picker previews.
const bannerGradient = computed(() =>
    resolveProfileTheme(props.user.profile_theme).gradient.replace(
        /linear-gradient\(\s*[\d.]+deg/,
        "linear-gradient(133deg",
    ),
);

const profileUrl = computed(() =>
    route("user.giftlists", { username: props.user.username }),
);

const avatarFailed = ref(false);

const counts = computed(() => [
    { icon: "lists", value: props.user.list_count ?? 0, label: "Lists" },
    {
        icon: "followers",
        value: props.user.followers_count ?? 0,
        label: "Followers",
    },
    {
        icon: "following",
        value: props.user.following_count ?? 0,
        label: "Following",
    },
]);

const countIconSizes = {
    lists: "h-[13px] w-[15px]",
    followers: "h-[14px] w-[19px]",
    following: "h-4 w-[18px]",
};

// Follow / unfollow. The endpoints return a bare boolean (not an Inertia
// response), so this goes through axios and reloads props afterwards —
// same pattern as the Explore bookmark toggle.
const { busy: followBusy, run: runFollow } = useInertiaAction();

async function toggleFollow() {
    if (followBusy.value) {
        return;
    }

    // Busy spans the request and the reload (GH-1159) so the button lands
    // directly in its new state.
    await runFollow(() =>
        props.user.following
            ? axios.delete(route("user.unfollow", { user: props.user.uuid }))
            : axios.post(route("user.follow", { user: props.user.uuid })),
    );
}
</script>

<template>
    <article
        class="flex flex-col rounded-[5px] border border-solid border-[#cdc4c2] bg-white p-4"
    >
        <div class="relative">
            <div
                class="h-[73px] rounded-[4px]"
                :style="{ backgroundImage: bannerGradient }"
            ></div>
            <span
                v-if="user.is_gift_hero"
                class="absolute left-2 top-2 block size-[30px] rounded-[2px] bg-black/40"
                title="Gift Hero"
            >
                <IconGiftHeroBug
                    class="absolute left-[7px] top-[6px] size-[17px]"
                    aria-hidden="true"
                />
                <IconSparkles
                    class="absolute -right-[6px] -top-[5px] size-[13px]"
                    aria-hidden="true"
                />
            </span>
            <span
                v-else-if="user.is_expert"
                class="absolute left-2 top-2 grid size-[30px] place-items-center rounded-[2px] bg-black/40"
                title="Expert"
            >
                <img
                    :src="asset('images/profile/badge-top-contributor.png')"
                    alt="Expert"
                    class="size-[15px]"
                />
            </span>
            <!-- The avatar leads to the profile like the name does (BH-56) -->
            <Link
                :href="profileUrl"
                class="absolute left-1/2 top-[25px] block size-[93px] -translate-x-1/2"
                :aria-label="`View ${user.presentation_name}'s profile`"
                data-test="friend-avatar-link"
            >
                <img
                    v-if="user.avatar?.profile_photo && !avatarFailed"
                    :src="user.avatar.profile_photo"
                    :alt="user.presentation_name"
                    class="size-full rounded-full border-[7px] border-solid border-white object-cover"
                    @error="avatarFailed = true"
                />
                <div
                    v-else
                    class="grid size-full place-items-center rounded-full border-[7px] border-solid border-white bg-brand-navy/80 font-brand-display text-[34px] font-black uppercase text-white"
                    aria-hidden="true"
                >
                    {{ user.presentation_name?.charAt(0) }}
                </div>
            </Link>
        </div>

        <Link
            :href="profileUrl"
            class="mt-12 text-center font-brand text-[22px] font-semibold leading-[1.2] text-[#102a3d] transition hover:text-brand-red"
        >
            {{ user.presentation_name }}
        </Link>

        <div
            class="mt-3.5 grid grid-cols-3 rounded-[2px] border border-solid border-[#e6dfdd] px-2 py-2.5 font-brand text-[13px] text-[#102a3d] md:text-[11px] lg:text-[13px]"
        >
            <span
                v-for="count in counts"
                :key="count.label"
                class="flex flex-col items-center gap-1"
            >
                <CountIcon
                    :icon="count.icon"
                    :class="countIconSizes[count.icon]"
                />
                <span class="whitespace-nowrap"
                    ><strong>{{ count.value }}</strong> {{ count.label }}</span
                >
            </span>
        </div>

        <!-- truncate clips overflow, so the line box needs room for
             descenders (BH-57) -->
        <p
            class="mt-5 truncate text-center font-brand text-[14px] leading-normal text-[#565a77]"
        >
            @{{ user.username }}
        </p>

        <!-- Standard primary CTAs (GH-999): same variant pairing as the
             rail FollowButton — navy at rest, white-on-red once following. -->
        <div class="mt-auto flex gap-2.5 pt-5">
            <BrandButton :href="profileUrl" class="h-11 flex-1 text-[15px]">
                Visit
            </BrandButton>
            <BrandButton
                v-if="!isSelf"
                :variant="user.following ? 'whiteOnRed' : 'navy'"
                class="h-11 flex-1 text-[15px]"
                :class="user.following && 'ring-1 ring-brand-tan'"
                :loading="followBusy"
                @click="toggleFollow"
            >
                {{
                    followBusy
                        ? user.following
                            ? "Unfollowing"
                            : "Following"
                        : user.following
                          ? "Following"
                          : "Follow"
                }}
            </BrandButton>
            <span
                v-else
                class="flex h-11 flex-1 items-center justify-center rounded-[5px] bg-brand-cream font-brand text-[15px] font-bold text-[#565a77]"
            >
                That’s Me!
            </span>
        </div>
    </article>
</template>
